import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { dashboardService } from '../../services/dashboardService';
import { ChartTooltip, EmptyState, PageHeader, SectionCard, StatCard, useChartTheme } from '../ui';

interface UserStat {
  month: string;
  newUsers: number;
}

interface LicenseStat {
  month: string;
  model: string;
  totalLicenses: number;
  totalDuration: number;
}

interface CountByType {
  type: string;
  count: number;
}

/** One row per month, with each product's figures flattened into columns. */
interface MonthlyLicenseRow {
  month: string;
  oneChangerLicenses: number;
  oneChangerDuration: number;
  miChangerLicenses: number;
  miChangerDuration: number;
}

interface DashboardData {
  userStats: UserStat[];
  licenseStats: MonthlyLicenseRow[];
  activeLicenses: CountByType[];
  allLicenses: CountByType[];
}

const CHART_HEIGHT = 300;

const sum = <T,>(rows: T[] | undefined, pick: (row: T) => number) =>
  (rows ?? []).reduce((total, row) => total + (pick(row) || 0), 0);

/** Recharts wants one row per X value, the API returns one row per product. */
const groupByMonth = (rows: LicenseStat[]): MonthlyLicenseRow[] => {
  const byMonth = new Map<string, MonthlyLicenseRow>();

  for (const row of rows ?? []) {
    const entry =
      byMonth.get(row.month) ??
      {
        month: row.month,
        oneChangerLicenses: 0,
        oneChangerDuration: 0,
        miChangerLicenses: 0,
        miChangerDuration: 0,
      };

    if (row.model === 'OneChanger') {
      entry.oneChangerLicenses = row.totalLicenses;
      entry.oneChangerDuration = row.totalDuration;
    } else if (row.model === 'MiChanger') {
      entry.miChangerLicenses = row.totalLicenses;
      entry.miChangerDuration = row.totalDuration;
    }

    byMonth.set(row.month, entry);
  }

  return Array.from(byMonth.values());
};

const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 1 }}>
    {[62, 84, 48, 96, 70, 88].map((height, index) => (
      <Skeleton key={index} variant="rounded" sx={{ flex: 1, height: `${height}%` }} />
    ))}
  </Box>
);

export const Dashboard: React.FC = () => {
  const theme = useTheme();
  const chart = useChartTheme();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stats = await dashboardService.getDashboardStats();
      setData({
        userStats: stats.userStats ?? [],
        licenseStats: groupByMonth(stats.licenseStats as LicenseStat[]),
        activeLicenses: stats.activeLicenses ?? [],
        allLicenses: stats.allLicenses ?? [],
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err instanceof Error ? err.message : 'Could not load dashboard statistics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(
    () => ({
      newUsers: sum(data?.userStats, (row) => row.newUsers),
      activity: sum(data?.licenseStats, (row) => row.oneChangerLicenses + row.miChangerLicenses),
      active: sum(data?.activeLicenses, (row) => row.count),
      all: sum(data?.allLicenses, (row) => row.count),
    }),
    [data],
  );

  const hasUserStats = (data?.userStats.length ?? 0) > 0;
  const hasLicenseStats = (data?.licenseStats.length ?? 0) > 0;
  const hasActiveLicenses = (data?.activeLicenses.length ?? 0) > 0;

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="Registrations, licence activity and the current active fleet across the last five months."
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          mb: 2.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard
          label="New users"
          value={totals.newUsers.toLocaleString()}
          hint="Last 5 months"
          icon={<PersonAddAltOutlinedIcon />}
          tone="primary"
          loading={loading}
        />
        <StatCard
          label="Licence activity"
          value={totals.activity.toLocaleString()}
          hint="Issued in the last 5 months"
          icon={<QueryStatsOutlinedIcon />}
          tone="info"
          loading={loading}
        />
        <StatCard
          label="Active licences"
          value={totals.active.toLocaleString()}
          hint="Currently valid"
          icon={<TaskAltOutlinedIcon />}
          tone="success"
          loading={loading}
        />
        <StatCard
          label="Total licences"
          value={totals.all.toLocaleString()}
          hint="Expired included"
          icon={<InventoryOutlinedIcon />}
          tone="secondary"
          loading={loading}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          mb: 2.5,
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
        }}
      >
        <SectionCard title="User registrations" description="New accounts per month">
          {loading ? (
            <ChartSkeleton />
          ) : !hasUserStats ? (
            <EmptyState
              title="No registrations yet"
              description="Once accounts are created they will appear here as a monthly trend."
              icon={<PersonAddAltOutlinedIcon />}
            />
          ) : (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <AreaChart data={data!.userStats} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="newUsersFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chart.series[0].color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={chart.series[0].color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...chart.grid} />
                <XAxis dataKey="month" {...chart.axis} />
                <YAxis allowDecimals={false} width={48} {...chart.axis} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: theme.palette.divider }} />
                <Area
                  type="monotone"
                  dataKey="newUsers"
                  name="New users"
                  stroke={chart.series[0].color}
                  strokeWidth={2}
                  fill="url(#newUsersFill)"
                  dot={{ r: 3, strokeWidth: 0, fill: chart.series[0].color }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard title="Active licences" description="Share by product type">
          {loading ? (
            <ChartSkeleton />
          ) : !hasActiveLicenses ? (
            <EmptyState
              title="No active licences"
              description="Issued licences that are still valid will be broken down here."
              icon={<VpnKeyOutlinedIcon />}
            />
          ) : (
            <Box sx={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <PieChart>
                  <Pie
                    data={data!.activeLicenses}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="46%"
                    innerRadius={64}
                    outerRadius={96}
                    paddingAngle={2}
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                  >
                    {data!.activeLicenses.map((entry, index) => (
                      <Cell
                        key={entry.type}
                        fill={chart.series[index % chart.series.length].color}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend {...chart.legend} />
                </PieChart>
              </ResponsiveContainer>
              {/* The donut hole carries the total so the eye does not have to add up slices. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '46%',
                  left: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Typography
                  className="tnum"
                  sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1 }}
                >
                  {totals.active.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  active
                </Typography>
              </Box>
            </Box>
          )}
        </SectionCard>
      </Box>

      <SectionCard
        title="Licence activity by product"
        description="Bars count licences issued; lines track total duration sold"
      >
        {loading ? (
          <ChartSkeleton />
        ) : !hasLicenseStats ? (
          <EmptyState
            title="No licence activity"
            description="Licences issued in the last five months will be charted here."
            icon={<VpnKeyOutlinedIcon />}
          />
        ) : (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT + 40}>
            <ComposedChart data={data!.licenseStats} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="month" {...chart.axis} />
              <YAxis yAxisId="count" allowDecimals={false} width={48} {...chart.axis} />
              <YAxis yAxisId="duration" orientation="right" width={48} {...chart.axis} />
              <Tooltip content={<ChartTooltip />} cursor={chart.cursor} />
              <Legend {...chart.legend} />
              <Bar
                yAxisId="count"
                dataKey="oneChangerLicenses"
                name="OneChanger licences"
                fill={chart.series[0].color}
                barSize={18}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="count"
                dataKey="miChangerLicenses"
                name="MiChanger licences"
                fill={chart.series[1].color}
                barSize={18}
                radius={[4, 4, 0, 0]}
              />
              {/* Dashes keep the two duration lines apart for colour-blind readers. */}
              <Line
                yAxisId="duration"
                type="monotone"
                dataKey="oneChangerDuration"
                name="OneChanger duration"
                stroke={chart.series[2].color}
                strokeWidth={2}
                strokeDasharray={chart.series[2].dash}
                dot={false}
              />
              <Line
                yAxisId="duration"
                type="monotone"
                dataKey="miChangerDuration"
                name="MiChanger duration"
                stroke={chart.series[3].color}
                strokeWidth={2}
                strokeDasharray={chart.series[3].dash}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      <Stack sx={{ mt: 2.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Figures cover the trailing five months and refresh on demand.
        </Typography>
      </Stack>
    </>
  );
};

export default Dashboard;
